import Phaser from 'phaser';
import { getCharacter } from '@/data/personajes';
import {
  elegirCharla,
  fraseAccion,
  INTERIOR_ACTIONS,
  MISION_REUNION_ID,
  STICKER_REUNION_TEXTO,
  type Charla,
  type InteriorAction,
} from '@/data/interior';
import { GameState } from '@/engine/GameState';
import { drawPortrait } from '@/engine/portrait';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  type MissionSceneData,
} from '@/engine/nav';

/** Paleta y constantes de layout coherentes con HubScene / MissionScene. */
const COLOR_FONDO_PARED = 0x3b2a1f; // pared cálida de madera oscurecida
const COLOR_FUEGO_BRASA = 0xd35400; // naranja brasa
const COLOR_FUEGO_LLAMARADA = 0xf39c12; // amarillo llama
const COLOR_ESTUFA = 0x2c2c38; // cuerpo oscuro de la estufa
const COLOR_ESTUFA_LLAMARADA = 0xe67e22; // llama de la estufa
const COLOR_MESA = 0x6b4423; // madera marrón mesa
const COLOR_MESA_BORDE = 0x3b2412; // borde mesa
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BOTON_DESTACADO = 0xc0392b; // rojo fuerte para el sticker
const COLOR_BOTON_DESTACADO_HOVER = 0xe74c3c;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TEXTO_DESTACADO = '#fff8e1';

const HUD_ALTO = 100; // debe coincidir con HudScene
const MARGEN = 24;

/** Modos internos del panel central. Cada acción abre uno distinto. */
type PanelMode =
  | { tipo: 'idle' }
  | { tipo: 'frase'; texto: string; actionId: InteriorAction['id'] }
  | { tipo: 'charla'; charla: Charla; lineaIndex: number }
  | { tipo: 'sticker' };

/**
 * Escena del INTERIOR DE LA CASA (ver `docs/biblia/escenarios.md`). Toda
 * la fraseología viene de `src/data/interior.ts`; aquí solo se dibuja:
 *  - ambiente dibujado por código (fuego a tierra, estufa, mesa grande),
 *  - botonera de acciones (sentarse / charlar / fumar / vídeos / tablet),
 *  - panel central para cada acción (las charlas avanzan por tap, igual
 *    que un diálogo de MissionScene),
 *  - botón destacado del sticker de WhatsApp que arranca la misión de la
 *    casa random (canon, ver `personajes.md`).
 *
 * Si la misión `la-casa-random` ya está completada, el botón del sticker
 * se sustituye por un texto pequeño "Misión completada ✅".
 */
export class InteriorCasaScene extends Phaser.Scene {
  /** Contenedor principal donde vive todo el UI re-renderizable. */
  private uiContainer!: Phaser.GameObjects.Container;
  /** Contenedor del ambiente (fondo, fuego, estufa, mesa): se dibuja una
   *  vez y NO se limpia al re-renderizar el panel. */
  private ambienteContainer!: Phaser.GameObjects.Container;

  /** Estado actual del panel central. */
  private panelMode: PanelMode = { tipo: 'idle' };

  constructor() {
    super(SCENE_KEYS.INTERIOR);
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO_PARED);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.ambienteContainer = this.add.container(0, 0);
    this.uiContainer = this.add.container(0, 0);

    this.dibujarAmbiente(width, height);
    this.renderUI();
  }

  // ---------------------------------------------------------------------
  // Ambiente (fuego a tierra, estufa, mesa grande, luz cálida)
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, height: number): void {
    // Pared cálida de fondo (gradient simulado con tres bandas).
    const fondo = this.add.graphics();
    fondo.fillStyle(COLOR_FONDO_PARED, 1);
    fondo.fillRect(0, 0, width, height);
    this.ambienteContainer.add(fondo);

    // Suelo más oscuro en la mitad inferior.
    const suelo = this.add.graphics();
    suelo.fillStyle(0x2a1d14, 1);
    suelo.fillRect(0, height * 0.62, width, height * 0.38);
    this.ambienteContainer.add(suelo);

    // Fuego a tierra en la esquina inferior izquierda.
    this.dibujarFuegoTierra(width * 0.18, height * 0.78);

    // Estufa en la esquina inferior derecha.
    this.dibujarEstufa(width * 0.82, height * 0.78);

    // Mesa grande central (elipse marrón) — la zona principal de la casa.
    this.dibujarMesaGrande(width / 2, height * 0.55, width * 0.6, 80);

    // Halo cálido alrededor del fuego y la estufa para vender la luz.
    const haloIzq = this.add.graphics();
    haloIzq.fillStyle(0xf39c12, 0.08);
    haloIzq.fillCircle(width * 0.18, height * 0.78, 180);
    this.ambienteContainer.add(haloIzq);
    const haloDer = this.add.graphics();
    haloDer.fillStyle(0xf39c12, 0.06);
    haloDer.fillCircle(width * 0.82, height * 0.78, 130);
    this.ambienteContainer.add(haloDer);
  }

  private dibujarFuegoTierra(cx: number, cy: number): void {
    // Base de brasas: mancha naranja irregular.
    const brasas = this.add.graphics();
    brasas.fillStyle(COLOR_FUEGO_BRASA, 1);
    brasas.fillCircle(cx, cy + 6, 28);
    brasas.fillStyle(0x6e2c00, 1);
    brasas.fillCircle(cx, cy + 10, 22);
    brasas.fillStyle(COLOR_FUEGO_LLAMARADA, 0.85);
    brasas.fillCircle(cx, cy - 4, 14);
    brasas.fillStyle(0xfff3b0, 0.6);
    brasas.fillCircle(cx, cy - 6, 6);
    this.ambienteContainer.add(brasas);

    // Troncos cruzados.
    const troncos = this.add.graphics();
    troncos.fillStyle(0x3b2412, 1);
    troncos.fillRoundedRect(cx - 22, cy + 4, 44, 8, 3);
    troncos.fillStyle(0x4a2f1a, 1);
    troncos.fillRoundedRect(cx - 22, cy - 4, 44, 6, 3);
    this.ambienteContainer.add(troncos);

    // Emoji "fuego" como remate visual (opcional, decorativo).
    const emoji = this.add
      .text(cx, cy - 32, '🔥', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
      })
      .setOrigin(0.5);
    this.ambienteContainer.add(emoji);
  }

  private dibujarEstufa(cx: number, cy: number): void {
    // Cuerpo rectangular de la estufa.
    const cuerpo = this.add.graphics();
    cuerpo.fillStyle(COLOR_ESTUFA, 1);
    cuerpo.fillRoundedRect(cx - 32, cy - 30, 64, 80, 6);
    cuerpo.lineStyle(2, 0x1a1a24, 1);
    cuerpo.strokeRoundedRect(cx - 32, cy - 30, 64, 80, 6);
    this.ambienteContainer.add(cuerpo);

    // Puerta con ventanita.
    const puerta = this.add.graphics();
    puerta.fillStyle(0x1a1a24, 1);
    puerta.fillRoundedRect(cx - 22, cy - 16, 44, 46, 3);
    this.ambienteContainer.add(puerta);

    // Llamarada dentro del cristal.
    const llama = this.add.graphics();
    llama.fillStyle(COLOR_ESTUFA_LLAMARADA, 0.9);
    llama.fillCircle(cx, cy + 8, 12);
    llama.fillStyle(0xfff3b0, 0.7);
    llama.fillCircle(cx, cy + 6, 5);
    this.ambienteContainer.add(llama);

    // Tubo de salida hacia arriba.
    const tubo = this.add.graphics();
    tubo.fillStyle(0x444452, 1);
    tubo.fillRect(cx - 6, cy - 60, 12, 30);
    this.ambienteContainer.add(tubo);
  }

  private dibujarMesaGrande(cx: number, cy: number, ancho: number, alto: number): void {
    // Elipse base de la mesa (vista cenital ligera).
    const mesa = this.add.graphics();
    mesa.fillStyle(COLOR_MESA, 1);
    mesa.fillEllipse(cx, cy, ancho, alto);
    mesa.lineStyle(3, COLOR_MESA_BORDE, 1);
    mesa.strokeEllipse(cx, cy, ancho, alto);
    this.ambienteContainer.add(mesa);

    // Tablero (rectángulo oscuro encima) — sugiere superficie.
    const tablero = this.add.graphics();
    tablero.fillStyle(0x5a3a1f, 1);
    tablero.fillRoundedRect(cx - ancho / 2 + 14, cy - alto / 2 + 8, ancho - 28, alto - 16, 6);
    this.ambienteContainer.add(tablero);

    // Velas / jarra decorativa en el centro (sin liar).
    const jarra = this.add.graphics();
    jarra.fillStyle(0x7f8c8d, 1);
    jarra.fillRoundedRect(cx - 6, cy - 8, 12, 18, 2);
    this.ambienteContainer.add(jarra);
  }

  // ---------------------------------------------------------------------
  // UI re-renderizable: sticker/misión arriba, panel central, acciones, volver
  // ---------------------------------------------------------------------

  private renderUI(): void {
    this.limpiarUI();

    this.renderBotonSticker();

    if (this.panelMode.tipo === 'idle') {
      this.renderPanelIdle();
    } else if (this.panelMode.tipo === 'frase') {
      this.renderPanelFrase(this.panelMode.texto, this.panelMode.actionId);
    } else if (this.panelMode.tipo === 'charla') {
      this.renderPanelCharla(this.panelMode.charla, this.panelMode.lineaIndex);
    } else if (this.panelMode.tipo === 'sticker') {
      this.renderPanelSticker();
    }

    this.renderBotoneraAcciones();
    this.renderBotonVolver();
  }

  private limpiarUI(): void {
    if (!this.uiContainer) return;
    this.uiContainer.removeAll(true);
  }

  // --- Botón del sticker / texto "misión completada" -------------------

  private renderBotonSticker(): void {
    const { width } = this.scale;
    const completada = GameState.misionCompletada(MISION_REUNION_ID);

    if (completada) {
      const texto = this.add
        .text(width / 2, HUD_ALTO + 50, 'Misión completada ✅', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5);
      this.uiContainer.add(texto);
      return;
    }

    const ancho = width - MARGEN * 2;
    const alto = 72;
    const y = HUD_ALTO + 50;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 18);
    };
    dibujar(COLOR_BOTON_DESTACADO);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, '📱 Mandar el sticker', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DESTACADO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON_DESTACADO));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerup', () => {
      this.panelMode = { tipo: 'sticker' };
      this.renderUI();
    });
    this.uiContainer.add(zona);
  }

  // --- Panel central: idle (bienvenida), frase (sentarse/fumar/...) ----

  private renderPanelIdle(): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 150;
    const cy = height * 0.38;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.92);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(width / 2, cy - 28, 'INTERIOR DE LA CASA', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + 6, 'La mesa grande, el fuego y la estufa.\nElige qué hacer.', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );
  }

  private renderPanelFrase(texto: string, actionId: InteriorAction['id']): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 220;
    const cy = height * 0.38;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.95);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    // Emoji + nombre de la acción arriba.
    const accion = INTERIOR_ACTIONS.find((a) => a.id === actionId);
    this.uiContainer.add(
      this.add
        .text(width / 2, cy - panelAlto / 2 + 38, accion?.emoji ?? '✨', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '32px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add
        .text(width / 2, cy - panelAlto / 2 + 80, accion?.nombre ?? '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    // Texto de la frase centrada.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + 30, texto, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );

    // Indicador ▼ abajo a la derecha + zona tap para cerrar y volver a idle.
    this.uiContainer.add(
      this.add
        .text(width / 2 + panelAncho / 2 - 30, cy + panelAlto / 2 - 24, '▼', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    const zonaTap = this.add
      .zone(width / 2, cy, panelAncho, panelAlto)
      .setInteractive({ useHandCursor: true });
    zonaTap.on('pointerup', () => {
      this.panelMode = { tipo: 'idle' };
      this.renderUI();
    });
    this.uiContainer.add(zonaTap);
  }

  // --- Panel central: charla (avance por tap como MissionScene) --------

  private renderPanelCharla(charla: Charla, lineaIndex: number): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 260;
    const cy = height * 0.38;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.96);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    // Título de la charla arriba.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy - panelAlto / 2 + 30, charla.titulo, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DIM,
          align: 'center',
          wordWrap: { width: panelAncho - 40 },
        })
        .setOrigin(0.5),
    );

    const linea = charla.lineas[lineaIndex];
    if (!linea) return;

    // Retrato a la izquierda (estilo MissionScene).
    const retratoX = width / 2 - panelAncho / 2 + 70;
    const retratoY = cy + 20;
    const retrato = drawPortrait(this, retratoX, retratoY, linea.personajeId, linea.emocion, {
      mostrarApodo: false,
      radio: 36,
      tamanoEmoji: '36px',
    });
    this.uiContainer.add(retrato);

    // Apodo en color del personaje + texto, a la derecha del retrato.
    const char = getCharacter(linea.personajeId);
    const colorHex = '#' + (char.color ?? 0x4a4a55).toString(16).padStart(6, '0');
    const textoX = width / 2 - panelAncho / 2 + 130;
    const textoAncho = panelAncho - 130 - 30;

    this.uiContainer.add(
      this.add.text(textoX, cy - 8, char.apodo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        fontStyle: 'bold',
        color: colorHex,
      }),
    );
    this.uiContainer.add(
      this.add.text(textoX, cy + 18, linea.texto, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: COLOR_TEXTO,
        wordWrap: { width: textoAncho },
      }),
    );

    // Indicador ▼ abajo a la derecha si quedan más líneas o ya es la última.
    const quedanLineas = lineaIndex < charla.lineas.length - 1;
    this.uiContainer.add(
      this.add
        .text(width / 2 + panelAncho / 2 - 30, cy + panelAlto / 2 - 24, '▼', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: quedanLineas ? COLOR_TEXTO_DIM : '#666670',
        })
        .setOrigin(0.5),
    );

    // Tap en cualquier sitio: avanza línea o cierra.
    const zonaTap = this.add
      .zone(width / 2, cy, panelAncho, panelAlto)
      .setInteractive({ useHandCursor: true });
    zonaTap.on('pointerup', () => {
      this.avanzarCharla();
    });
    this.uiContainer.add(zonaTap);
  }

  private avanzarCharla(): void {
    if (this.panelMode.tipo !== 'charla') return;
    const { charla, lineaIndex } = this.panelMode;
    if (lineaIndex < charla.lineas.length - 1) {
      this.panelMode = { tipo: 'charla', charla, lineaIndex: lineaIndex + 1 };
    } else {
      // Última línea: vuelve a idle.
      this.panelMode = { tipo: 'idle' };
    }
    this.renderUI();
  }

  // --- Panel central: sticker (canon) ---------------------------------

  private renderPanelSticker(): void {
    const { width, height } = this.scale;
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 320;
    const cy = height * 0.38;

    // Panel tipo "burbuja de WhatsApp": verde claro sobre fondo oscuro.
    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.96);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    panel.setPosition(width / 2, cy);
    this.uiContainer.add(panel);

    // Burbuja verde WhatsApp con el sticker canon.
    const burbujaAncho = panelAncho - 60;
    const burbujaAlto = 110;
    const burbujaX = width / 2;
    const burbujaY = cy - 30;
    const burbuja = this.add.graphics();
    burbuja.fillStyle(0x075e54, 1);
    burbuja.fillRoundedRect(
      -burbujaAncho / 2,
      -burbujaAlto / 2,
      burbujaAncho,
      burbujaAlto,
      16,
    );
    this.uiContainer.add(burbuja);
    this.uiContainer.add(
      this.add
        .text(burbujaX, burbujaY, STICKER_REUNION_TEXTO, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: '#ffffff',
          align: 'center',
          wordWrap: { width: burbujaAncho - 30 },
        })
        .setOrigin(0.5),
    );

    // Botón SALIR A LA CASA RANDOM que lanza la misión.
    const anchoBoton = panelAncho - 60;
    const altoBoton = 70;
    const botonY = cy + 80;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-anchoBoton / 2, -altoBoton / 2, anchoBoton, altoBoton, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, botonY);
    this.uiContainer.add(boton);
    this.uiContainer.add(
      this.add
        .text(width / 2, botonY, 'SALIR A LA CASA RANDOM', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add
      .zone(width / 2, botonY, anchoBoton, altoBoton)
      .setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      const data: MissionSceneData = { missionId: MISION_REUNION_ID };
      goTo(this, SCENE_KEYS.MISSION, data);
    });
    this.uiContainer.add(zona);

    // Texto pequeño "toca para cerrar" en la parte inferior del panel.
    this.uiContainer.add(
      this.add
        .text(width / 2, cy + panelAlto / 2 - 22, 'toca el panel para cerrar', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
        })
        .setOrigin(0.5),
    );

    // Zona tap en la parte superior del panel (fuera del botón) para cerrar.
    const zonaCerrar = this.add
      .zone(width / 2, cy - 40, panelAncho, 130)
      .setInteractive({ useHandCursor: true });
    zonaCerrar.on('pointerup', () => {
      this.panelMode = { tipo: 'idle' };
      this.renderUI();
    });
    this.uiContainer.add(zonaCerrar);
  }

  // --- Botonera de acciones -------------------------------------------

  private renderBotoneraAcciones(): void {
    const { width, height } = this.scale;
    const areaTop = height * 0.58;
    const areaBottom = height - 110; // deja sitio al botón Volver
    const gap = 10;
    const ancho = width - MARGEN * 2;
    const altoDisponible = areaBottom - areaTop;
    const botonAlto = (altoDisponible - gap * (INTERIOR_ACTIONS.length - 1)) / INTERIOR_ACTIONS.length;

    INTERIOR_ACTIONS.forEach((accion, i) => {
      const y = areaTop + i * (botonAlto + gap) + botonAlto / 2;
      this.crearBotonAccion(accion, width / 2, y, ancho, botonAlto);
    });
  }

  private crearBotonAccion(
    accion: InteriorAction,
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    // Emoji a la izquierda.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 32, y, accion.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
        })
        .setOrigin(0, 0.5),
    );
    // Nombre en negrita + descripción debajo.
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 76, y - 9, accion.nombre, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO,
        })
        .setOrigin(0, 0.5),
    );
    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 76, y + 11, accion.descripcion, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_DIM,
          wordWrap: { width: ancho - 100 },
        })
        .setOrigin(0, 0.5),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.manejarAccion(accion));
    this.uiContainer.add(zona);
  }

  private manejarAccion(accion: InteriorAction): void {
    if (accion.id === 'charlar') {
      this.panelMode = { tipo: 'charla', charla: elegirCharla(), lineaIndex: 0 };
    } else {
      this.panelMode = { tipo: 'frase', texto: fraseAccion(accion.id), actionId: accion.id };
    }
    this.renderUI();
  }

  // --- Botón Volver al barrio -----------------------------------------

  private renderBotonVolver(): void {
    const { width, height } = this.scale;
    const ancho = 260;
    const alto = 60;
    const y = height - 50;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, '← Volver al barrio', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      goTo(this, SCENE_KEYS.HUB);
    });
    this.uiContainer.add(zona);
  }
}
